import { SqlTableDefinition } from "@cervval.js/common-sql-core";
import { CommonEventHandler } from "@cervval.js/common/src/tools/event.tools";
import { ActionPersistResult } from "../action.model";
import { DTOCacheRepository } from "../dtos/dtoCacheRepository";

export const ACTION_CACHE_SERVICE = "ActionCacheService";
export interface ActionCacheService<ElementTypes, CacheInfo = unknown> {

    /**
     * Get the table definition map used in cache
     * @returns {Map<keyof ElementTypes, SqlTableDefinition>}
     */
    getTableDefinitionMap(): Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, string>>;

    /**
     * Get the Table definition of a given type name
     * @param elementTypeName 
     * @returns {SqlTableDefinition} or throws an exception
     */
    getTableDefinition(elementTypeName: keyof ElementTypes): SqlTableDefinition<keyof ElementTypes, string> | undefined;

    /**
     * Get the foreign table to read upToDateId from (or null if not applicable).
     * Works on both standard properties and associative properties.
     * @returns The foreign table name or null if elementTypeName.fieldName is not a foreign key or column is not known.
     * 
     * Shortcut to function of @cervval.js/sql-core. Allows to add cache.
     */
    getForeignKeyTableName<ET extends keyof ElementTypes>(elementTypeName: ET, fieldName: keyof ElementTypes[ET]): keyof ElementTypes | null;

    /**
     * Get the DTOCacheRepository for a given type name
     * @returns {DTOCacheRepository} or throws an exception
     */
    getDTOCacheRepository<TypeName extends keyof ElementTypes, ElementType extends ElementTypes[TypeName]>(elementTypeName: TypeName): DTOCacheRepository<ElementType, CacheInfo>;

    /**
     * find the DTOCacheRepository for a given type name
     * @returns {DTOCacheRepository} or undefined
     */
    findDTOCacheRepository<TypeName extends keyof ElementTypes, ElementType extends ElementTypes[TypeName]>(elementTypeName: TypeName): DTOCacheRepository<ElementType, CacheInfo> | undefined;

    /**
     * Set a temporary id on an element
     * @param elementTypeName 
     * @param elementToPrepare 
     */
    setTemporaryId<TypeName extends keyof ElementTypes, ElementType extends ElementTypes[TypeName]>(elementTypeName: TypeName, elementToPrepare: ElementType): void;

    /**
     * Update cache using action persistence server results
     * @param actionPersistResult 
     */
    updatePersistedElements(actionPersistResult: ActionPersistResult): void;

    /** @CommonEvent triggered when the whole cache needs to be tagged dirty (for example on persistenceError) */
    cacheDirty: CommonEventHandler<{ reason: "persistenceError" | string }>;

    /** @CommonEvent triggered when the "refresh button" state needs to be changed (on error or after websocket message) */
    dirtyStateChanged: CommonEventHandler<void>;

    /** 
     * cleanup memory for garbage collector to do its job 
     * once disposed, this service is no more in a usable state (it is "destroyed")
     */
    dispose(p: { disposeRepositories: boolean }): void;
}
